import { type CardSeen, type MatchDetail } from '@/api/types';

/**
 * Turn a reconstructed match into a question for Ugin.
 *
 * Every clause is a fact from the match record — what was played, when it was
 * seen, how the life totals ended. Nothing is inferred and nothing is invented,
 * so the chat answer is grounded in the game that actually happened.
 */

const RESULT_VERB: Readonly<Record<string, string>> = {
  win: 'won',
  loss: 'lost',
  concede: 'lost',
  draw: 'drew',
};

const UNKNOWN_REASON: Readonly<Record<string, string>> = {
  no_centroids: 'the archetype data for this format has not been built yet',
  no_cards: 'they never revealed a card',
  too_few_matches: 'too few distinctive cards showed up',
  ambiguous: 'the evidence split evenly between several archetypes',
};

function mulliganPhrase(count: number, who: 'me' | 'opp'): string {
  const subject = who === 'me' ? 'I' : 'they';
  const possessive = who === 'me' ? 'my' : 'their';
  if (count === 0) return `${subject} kept ${possessive} opening seven`;
  if (count === 1) return `${subject} mulliganed once`;
  if (count === 2) return `${subject} mulliganed twice`;
  return `${subject} mulliganed ${String(count)} times`;
}

function spellsOf(cards: readonly CardSeen[]): CardSeen[] {
  // Lands are noise in a review: every deck plays them and they say nothing
  // about what the opponent was doing.
  return cards.filter((c) => !c.isLand && c.cardName !== null);
}

function nameWithTurn(card: CardSeen): string {
  const turn = card.firstPublicTurn ?? card.firstSeenTurn;
  return `${card.cardName ?? 'an unknown card'} (turn ${String(turn)})`;
}

export function buildGamePrompt(match: MatchDetail): string {
  const lines: string[] = [];
  const verb = RESULT_VERB[match.result] ?? 'played';
  const opponent = match.opponentName ?? 'an unnamed opponent';
  const rank = match.opponentRank === null ? '' : ` (${match.opponentRank})`;
  const deck = match.deckName === null ? '' : ` playing ${match.deckName}`;
  lines.push(`I ${verb} a ${match.format} match against ${opponent}${rank}${deck}.`);

  const game = match.games[0];
  if (game !== undefined) {
    const side = game.onPlay === null ? null : game.onPlay ? 'on the play' : 'on the draw';
    const clauses = [
      ...(side === null ? [] : [`I was ${side}`]),
      mulliganPhrase(game.myMulligans, 'me'),
      mulliganPhrase(game.oppMulligans, 'opp'),
    ];
    lines.push(
      `${clauses.join(', ')}. It ran ${String(game.turns)} turns and life ended ${String(game.finalLife.me)} to ${String(game.finalLife.opp)}.`,
    );

    const theirs = spellsOf(game.cards.opp);
    if (theirs.length > 0) {
      lines.push(`They showed: ${theirs.map(nameWithTurn).join(', ')}.`);
    }
    const mine = spellsOf(game.cards.me);
    if (mine.length > 0) {
      lines.push(`I held or cast: ${mine.map((c) => c.cardName ?? '').join(', ')}.`);
    }
  }

  const classification = match.classification;
  if (classification?.kind === 'classified') {
    const evidence = classification.evidence.map((e) => e.card).join(', ');
    const confidence = `${String(Math.round(classification.confidence * 100))}%`;
    lines.push(
      evidence === ''
        ? `Ugin read them as ${classification.archetype} at ${confidence} confidence.`
        : `Ugin read them as ${classification.archetype} at ${confidence} confidence, from ${evidence}.`,
    );
    const runnerUp = classification.alternatives[0];
    if (runnerUp !== undefined) lines.push(`The next best fit was ${runnerUp.archetype}.`);
  } else if (classification?.kind === 'unknown') {
    const reason = UNKNOWN_REASON[classification.reason] ?? 'the evidence was not strong enough';
    lines.push(`Ugin could not classify their deck: ${reason}.`);
  }

  lines.push('Walk me through what happened here, and what should I take from it?');
  return lines.join('\n\n');
}
